import { applySeo } from "@/lib/seo-head";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { queryOptions, useSuspenseQuery } from "@tanstack/react-query";
import { Layout } from "@/components/site/Layout";
import { OfficeMap } from "@/components/site/OfficeMap";
import { Phone, Mail, MapPin } from "lucide-react";
import { getPublicSettings, listAllProductsPublic } from "@/lib/content.functions";
import { ConsultationBookingForm } from "@/components/forms/ConsultationBookingForm";
import { DefaultContactForm } from "@/components/forms/DefaultContactForm";
import { getFormByKey } from "@/lib/forms/registry";
import { useT } from "@/lib/i18n";

const settingsQueryOptions = queryOptions({
  queryKey: ["public-settings"],
  queryFn: () => getPublicSettings(),
});

const productsQueryOptions = queryOptions({
  queryKey: ["all-products"],
  queryFn: () => listAllProductsPublic(),
});

type ContactSearch = { product?: string };

export const Route = createFileRoute("/contact")({
  component: Booking,
  validateSearch: (search: Record<string, unknown>): ContactSearch => ({
    product: typeof search.product === "string" ? search.product : undefined,
  }),
  beforeLoad: ({ search }) => {
    if (search.product) {
      throw redirect({
        to: "/products/$slug",
        params: { slug: search.product },
        replace: true,
      });
    }
  },
  loader: ({ context }) =>
    Promise.all([
      context.queryClient.ensureQueryData(settingsQueryOptions),
      context.queryClient.ensureQueryData(productsQueryOptions),
    ]),
  errorComponent: ({ error }) => (
    <Layout>
      <div className="max-w-2xl mx-auto px-6 py-24 text-center" role="alert">
        <h1 className="font-serif text-3xl mb-4">No se pudo cargar el formulario</h1>
        <p className="text-muted-foreground">{(error as Error).message}</p>
      </div>
    </Layout>
  ),
  notFoundComponent: () => (
    <Layout>
      <div className="max-w-2xl mx-auto px-6 py-24 text-center">
        <h1 className="font-serif text-3xl">Página no encontrada</h1>
      </div>
    </Layout>
  ),
  head: (ctx) => applySeo(ctx.matches, ((_c: unknown) => ({
    meta: [
      { title: "Reservar consulta — RR Lex Juristas" },
      {
        name: "description",
        content:
          "Reserva tu consulta jurídica de 45 minutos con RR Lex Juristas en Sevilla. Selecciona fecha, hora y confirma con pago seguro.",
      },
      { property: "og:title", content: "Reservar consulta jurídica — RR Lex Juristas" },
      {
        property: "og:description",
        content:
          "Agenda 45 minutos con una abogada de RR Lex Juristas en Sevilla. Confirmación inmediata por email tras el pago seguro.",
      },
      { property: "og:url", content: "https://rrlexjuristas.com/contact" },
    ],
    links: [{ rel: "canonical", href: "https://rrlexjuristas.com/contact" }],
  }))(ctx)),
});

function Booking() {
  const { tk } = useT();
  const { data: settings } = useSuspenseQuery(settingsQueryOptions);
  const { data: productsData } = useSuspenseQuery(productsQueryOptions);
  const { product: productSlug } = Route.useSearch();

  const selectedProduct = productSlug
    ? (productsData.products ?? []).find((p) => p.slug === productSlug && p.active) ?? null
    : null;
  const selectedFormDef = selectedProduct ? getFormByKey(selectedProduct.form_key) : null;

  const consultationProducts = (productsData.products ?? [])
    .filter((p) => p.form_key === "booking-consultation" && p.active === true)
    .sort((a, b) => a.sort_order - b.sort_order);
  const consultationProduct = consultationProducts[0] ?? null;
  const paymentEnabled =
    (settings.settings.consultation_payment_enabled ?? true) && Boolean(consultationProduct);

  const renderForm = () => {
    if (selectedProduct && selectedFormDef) {
      const FormComponent = selectedFormDef.component;
      return <FormComponent product={selectedProduct} />;
    }
    if (paymentEnabled && consultationProduct) {
      return <ConsultationBookingForm product={consultationProduct} products={consultationProducts} />;
    }
    return <DefaultContactForm />;
  };

  const showHeroIntro = !selectedProduct && !paymentEnabled;

  return (
    <Layout>
      {showHeroIntro && (
        <section className="border-b border-gold/15">
          <div className="max-w-7xl mx-auto px-6 py-16 md:py-24 text-center">
            <span className="text-xs tracking-[0.4em] uppercase text-gold">
              {tk("contacto.d8a53e", "Contacto")}
            </span>
            <h1 className="font-serif text-5xl md:text-6xl mt-5">
              {tk("contacto.5f4926", "Hablemos de ")}
              <em className="not-italic gold-gradient">{tk("contacto.2b8eb7", "su caso")}</em>
            </h1>
            <p className="mt-6 text-muted-foreground max-w-2xl mx-auto leading-relaxed">
              {tk("contacto.fc3bcc", "Envíenos sus datos y el motivo de su consulta. Nos pondremos en contacto con usted lo antes posible para coordinar una cita.")}
            </p>
          </div>
        </section>
      )}

      {renderForm()}

      <section className="border-t border-gold/15 bg-card/20">
        <div className="max-w-7xl mx-auto px-6 py-16 grid md:grid-cols-3 gap-8">
          {[
            { i: Phone, l: tk("contacto.26176f", "Teléfonos"), v: ["+34 665 649 354", "+34 954 13 66 37"] },
            { i: Mail, l: "Email", v: ["rrlexjuristas@icasevilla.org"] },
            { i: MapPin, l: tk("contacto.4e38ee", "Despachos"), v: ["Camas, Sevilla", "Calle Sierpes 48, Sevilla"] },
          ].map(({ i: Icon, l, v }) => (
            <div key={l} className="flex gap-4">
              <Icon size={20} className="text-gold mt-1 shrink-0" strokeWidth={1.3} />
              <div>
                <div className="text-[10px] tracking-[0.3em] uppercase text-gold/80">{l}</div>
                {v.map((line) => (
                  <div key={line} className="text-foreground/85 mt-1 text-sm">
                    {line}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>
      <OfficeMap />
    </Layout>
  );
}
